// A link kept on a record like a file (attachments spec §5.3): the ONE client path for both ways in,
// the Add link sheet and a URL dropped on the Files tab (Aaron 2026-10-21, "Accept dropped URLs").
// Both send POST /v6/attachments/link, so the server's rules apply to both (kinds.accept_link:
// http/https with a host, no user:password, no spaces or control characters; it stores the address
// and never fetches it), and a refusal reads the same either way.
import { AttachmentService, objectsUnavailable } from "./AttachmentService";
import type { RecordFile } from "./types";

/** What the user is told when the server will not keep the link: its own words where it gave them. */
export function linkRefusal(e: unknown): string {
  if (objectsUnavailable(e)) return "Links can't be added on this site until its upgrade is applied";
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === "string" ? detail : "The link was not added";
}

export function addLinkToRecord(args: {
  url: string;
  title?: string;
  source: string;
  id: number;
}): Promise<RecordFile> {
  const title = args.title?.trim();
  return AttachmentService.addLink({
    url: args.url.trim(),
    ...(title ? { title } : {}),
    source_type: args.source,
    source_id: args.id,
  });
}

/** An http(s) address, or null. A pre-check only, so a drop of plain text is not sent as a link;
 *  the server's accept_link stays the rule. */
function httpLink(raw: string | undefined | null): string | null {
  const text = (raw ?? "").trim();
  if (!text || /\s/.test(text)) return null;
  try {
    const url = new URL(text);
    return url.protocol === "http:" || url.protocol === "https:" ? text : null;
  } catch {
    return null;
  }
}

/** The link a drag carries, or null: `text/uri-list` first (its first non-comment line), then
 *  `text/plain` when it is one http(s) address. A drag that carries files is never a link: files
 *  dropped in upload as before. */
export function droppedLink(dt: DataTransfer | null | undefined): string | null {
  if (!dt) return null;
  if (dt.files && dt.files.length > 0) return null;
  const uriList = (dt.getData("text/uri-list") || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .find((line) => line && !line.startsWith("#"));
  return httpLink(uriList) ?? httpLink(dt.getData("text/plain"));
}
